import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, AlertCircle, ChevronRight, Briefcase, Clock, Hourglass, Star, Zap, Check, ArrowLeft } from 'lucide-react';
import GlobalLoader from './GlobalLoader';
import scheduleService from '../services/scheduleService';
import { groupSlotsByDuration, filterBookableSlots, BookingMode, extractDurationMinutes } from '../services/scheduleModels';

// ─────────────────────────────────────────────────────────────────────────────
// CalendarSelection — modo "Agendar por fecha"
//
// Replica el flujo Flutter `ScheduleByDateUnifiedPage`:
//   • Chip-rail horizontal con próximos 14 días
//   • Picker de duración (con badge "Recomendado")
//   • Lista vertical de slot-cards horizontales con nombre de doctora,
//     hora inicio/fin y badge "Bajo confirmación" si es on-demand
//
// Llama al endpoint unificado `create_include_consultation_by_user_v2` con
// `unified_view=true, source="daisies"` (con fallback al endpoint público).
// ─────────────────────────────────────────────────────────────────────────────

const TZ_DEFAULT = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City'; }
  catch (e) { return 'America/Mexico_City'; }
})();

const formatYmd = (date) => date.toISOString().split('T')[0];

const DAYS_TO_SHOW = 14;
const generateNextDays = (n = DAYS_TO_SHOW) => {
  const today = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });
};

const DAY_LABELS_SHORT_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_LABELS_LONG_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const CalendarSelection = ({
  selectedDate,
  selectedSlot,
  onDateSelect,
  onTimeClick,
  onBack,
  setSelectedSlot,
  hasMembership = false,
  specialty,
  authToken,
  userId,
}) => {
  const next14Days = useMemo(() => generateNextDays(DAYS_TO_SHOW), []);

  // ─── Duraciones (sólo NO-extras: el picker es "30 vs 60 min", no "incluida vs extra") ───
  // Primero intentamos cargar las duraciones desde `dais.specialty.get_model_data`
  // (lo que usa la app Flutter) porque ahí vienen con los minutos reales.
  // Si por algún motivo falla, caemos a `specialty.allDurations` del campaign
  // y a `extractDurationMinutes` para reconstruir los minutos.
  const [remoteDurations, setRemoteDurations] = useState(null);

  useEffect(() => {
    if (!specialty?.id) return;
    let cancelled = false;
    scheduleService.getSpecialtyData({ specialtyId: specialty.id, authToken }).then(data => {
      if (cancelled || !data) return;
      const list = Array.isArray(data.durations) ? data.durations
        : Array.isArray(data?.specialty?.durations) ? data.specialty.durations
        : null;
      if (list && list.length > 0) setRemoteDurations(list);
    });
    return () => { cancelled = true; };
  }, [specialty?.id, authToken]);

  const durationOptions = useMemo(() => {
    if (!specialty) return [];

    // Prioridad: 1) remote durations (con minutos garantizados)
    //            2) specialty.allDurations del campaign
    //            3) specialty.durations crudas desde Firebase
    const rawList = (Array.isArray(remoteDurations) && remoteDurations.length > 0)
      ? remoteDurations
      : ((specialty.allDurations && specialty.allDurations.length > 0)
          ? specialty.allDurations
          : (specialty.durations || []));

    const items = [];
    const seen = new Set();

    rawList.forEach(d => {
      if (!d || !d.id) return;
      // Excluimos las extras del picker — la pestaña "Consulta extra" se ve
      // de otra forma (flujo doctor / pago extra). Aquí sólo "30 vs 60 min".
      if (d.is_extra_duration === true || d.isExtraDuration === true) return;
      if (seen.has(d.id)) return;
      seen.add(d.id);
      const minutes = extractDurationMinutes(d);
      items.push({
        id: Number(d.id),
        isExtra: false,
        durationMinutes: minutes,
        name: d.name || (minutes ? `${minutes} min` : 'Consulta'),
      });
    });

    // Si después de filtrar quedamos sin nada útil, mostramos un fallback con
    // la duración principal de la especialidad (al menos para no romper el UI).
    if (items.length === 0 && specialty.duration) {
      items.push({
        id: Number(specialty.duration),
        isExtra: false,
        durationMinutes: null,
        name: 'Consulta',
      });
    }

    // Ordenar descendente por minutos → la larga (60) queda a la izquierda y
    // es el "Recomendado" por defecto.
    items.sort((a, b) => (b.durationMinutes || 0) - (a.durationMinutes || 0));
    return items;
  }, [specialty, remoteDurations]);

  // Sub-copy contextual según duración. La larga = "Consulta principal";
  // las cortas = "Solo para seguimientos". Sin necesidad de explicación.
  const subCopyForDuration = (dur, isLongest) => {
    if (!dur) return '';
    if (isLongest) return 'Consulta principal';
    return 'Solo para seguimientos';
  };

  // Por defecto seleccionamos la primera duración (la larga, "Recomendada").
  const [activeDurationId, setActiveDurationId] = useState(null);
  useEffect(() => {
    if (durationOptions.length > 0 && !activeDurationId) {
      setActiveDurationId(durationOptions[0].id);
    }
  }, [durationOptions, activeDurationId]);

  const activeDuration = useMemo(
    () => durationOptions.find(d => d.id === activeDurationId) || durationOptions[0] || null,
    [durationOptions, activeDurationId]
  );

  // ─── Cache por (date, durationId) ───
  // Key: `${dateString}::${durationId}`
  const [scheduleCache, setScheduleCache] = useState({});
  const cacheKey = (ds, durId) => `${ds}::${durId}`;

  // Procesa una lista de slots atómicos del backend → flat slots por doctora.
  // Sale del componente para reutilizar dentro del refactor de fetchSchedule.
  const buildFlatSlots = (rawSlots, duration, targetMin, atomicMin) => {
    const bookingSlots = groupSlotsByDuration({
      slots: rawSlots,
      targetDurationMinutes: targetMin,
      atomicSlotMinutes: atomicMin,
    }).map(bs => ({
      ...bs,
      durationId: duration.id,
      isExtraDuration: duration.isExtra,
      durationMinutes: duration.durationMinutes,
    }));

    const flat = [];
    bookingSlots.forEach(bs => {
      const profs = Array.isArray(bs.professionalIds) ? bs.professionalIds : [];
      if (profs.length === 0) {
        flat.push({ ...bs, doctor: null });
      } else {
        profs.forEach(prof => flat.push({ ...bs, doctor: prof }));
      }
    });

    // Orden: 1) "por confirmación" (on_demand) al final; 2) dentro del mismo
    // grupo, los garantizados de disponibilidad inmediata (is_locked = staff-
    // loaded) primero; 3) por hora ascendente.
    flat.sort((a, b) => {
      const aOnDemand = a.bookingMode === BookingMode.ON_DEMAND ? 1 : 0;
      const bOnDemand = b.bookingMode === BookingMode.ON_DEMAND ? 1 : 0;
      if (aOnDemand !== bOnDemand) return aOnDemand - bOnDemand;
      if (a.isLocked !== b.isLocked) return a.isLocked ? -1 : 1;
      return (a.hourStart || '').localeCompare(b.hourStart || '');
    });
    return flat;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // fetchSchedule — el endpoint create_include_consultation_by_user_v2 devuelve
  // varios días (los siguientes con horarios). En lugar de descartarlos y
  // mostrar "sin horarios" si el día pedido no aparece, **cacheamos TODOS los
  // días de la respuesta**. Así el usuario puede navegar el chip rail entre
  // días sin hacer otra llamada y los días con horarios se muestran tal cual
  // los devuelve el backend (coincide con el patrón de Flutter).
  //
  // Si el día pedido NO está en la respuesta, lo marcamos como vacío para que
  // el EmptyState pueda ofrecer "Ver siguiente día disponible".
  // ─────────────────────────────────────────────────────────────────────────
  const fetchSchedule = async (dateString, duration) => {
    if (!dateString || !specialty?.id || !duration?.id) return;
    const key = cacheKey(dateString, duration.id);
    if (scheduleCache[key] && !scheduleCache[key].error) return;

    setScheduleCache(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true, error: false } }));

    try {
      const ws = await scheduleService.getScheduleByDateWithFallback({
        specialtyId: specialty.id,
        durationId: duration.id,
        userId: userId || undefined,
        date: dateString,
        isExtra: duration.isExtra,
        authToken: authToken || '',
        tz: TZ_DEFAULT,
      });

      const targetMin = duration.durationMinutes || ws.slotDurationMinutes || 0;
      const atomicMin = ws.slotDurationMinutes || targetMin;

      // Construimos un update con TODOS los días devueltos por la API.
      const cacheUpdates = {};
      (ws.days || []).forEach(day => {
        if (!day?.date) return;
        cacheUpdates[cacheKey(day.date, duration.id)] = {
          // Solo horarios agendables: sin pasados ni dentro del mínimo de
          // anticipación (>= now + 6h), igual que el backend.
          slots: filterBookableSlots(day.date, buildFlatSlots(day.slots || [], duration, targetMin, atomicMin)),
          loading: false,
          error: false,
          weekDay: day.weekDay,
        };
      });

      // El día pedido se asegura como entrada (vacío si el backend no lo
      // devolvió) para que el cache refleje "ya intentamos, no hay horarios"
      // y no haga loops de re-fetch.
      if (!cacheUpdates[key]) {
        cacheUpdates[key] = { slots: [], loading: false, error: false };
      }

      setScheduleCache(prev => ({ ...prev, ...cacheUpdates }));
    } catch (err) {
      console.warn('[CalendarSelection.fetchSchedule] error', err);
      setScheduleCache(prev => ({ ...prev, [key]: { slots: [], loading: false, error: true } }));
    }
  };

  // ─── Auto-select primera fecha al montar (UX como la app: arranca con hoy) ───
  // Reemplaza el viejo accordion donde el usuario tenía que clickear para abrir.
  useEffect(() => {
    if (!selectedDate && next14Days.length > 0 && specialty?.id) {
      onDateSelect(next14Days[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialty?.id]);

  const selectedDateString = selectedDate ? formatYmd(selectedDate) : null;

  // Fetch cada vez que cambia fecha o duración.
  useEffect(() => {
    if (selectedDateString && activeDuration?.id) {
      fetchSchedule(selectedDateString, activeDuration);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateString, activeDuration?.id, specialty?.id]);


  // ─── Auto-scroll el chip rail para mantener visible la fecha seleccionada ───
  const chipRailRef = useRef(null);
  const selectedChipRef = useRef(null);
  // Refs por día para hacer scroll a su sección en la lista apilada.
  const dayAnchorRefs = useRef({});

  useEffect(() => {
    if (selectedChipRef.current) {
      selectedChipRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedDateString]);

  const currentCache = selectedDateString && activeDuration
    ? scheduleCache[cacheKey(selectedDateString, activeDuration.id)]
    : null;
  const isLoading = currentCache?.loading;
  const hasError = currentCache?.error;

  // ─── Días apilados: arma la lista de TODOS los días con horarios cacheados ───
  // (filtrados a los próximos 14 desde hoy, ordenados ascendente).
  // El patrón Flutter es: una sola fetch → render apilado de todos los días que
  // el backend devolvió. El chip rail solo navega con scroll.
  const stackedDays = useMemo(() => {
    if (!activeDuration?.id) return [];
    const result = [];
    next14Days.forEach((dateObj) => {
      const ds = formatYmd(dateObj);
      const cache = scheduleCache[cacheKey(ds, activeDuration.id)];
      if (cache && !cache.loading && !cache.error && Array.isArray(cache.slots) && cache.slots.length > 0) {
        result.push({ date: ds, weekDay: cache.weekDay, slots: cache.slots });
      }
    });
    return result;
  }, [scheduleCache, activeDuration?.id, next14Days]);

  // Si el usuario aterriza en un día sin slots (backend lo omitió), saltamos
  // al primer día con horarios. Solo se dispara una vez por (especialidad ×
  // duración) para evitar loops.
  const initialJumpRef = useRef(null);
  useEffect(() => {
    if (!stackedDays.length || !specialty?.id || !activeDuration?.id) return;
    const jumpKey = `${specialty.id}-${activeDuration.id}`;
    if (initialJumpRef.current === jumpKey) return;

    const currentDayInStack = stackedDays.some(d => d.date === selectedDateString);
    if (currentDayInStack) {
      initialJumpRef.current = jumpKey;
      return;
    }

    initialJumpRef.current = jumpKey;
    const first = stackedDays[0];
    const dateObj = new Date(first.date + 'T12:00:00');
    onDateSelect(dateObj);
    setTimeout(() => {
      const el = dayAnchorRefs.current[first.date];
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackedDays, specialty?.id, activeDuration?.id]);

  // Construir el payload del slot al click (compat. con el resto del flujo).
  // `dayDate` viene de la sección apilada — cada slot conoce su día propio.
  // NOTA: `isLocked: true` en el backend NO significa "no agendable" — significa
  // que el horario fue cargado por el staff de Daisies (no por la doctora
  // directamente) y por tanto está GARANTIZADO. Es 100% bookable.
  const handleSlotClick = (bs, dayDate) => {
    const isExtraSlot = bs.isExtraDuration || bs.isExtra;
    const doctor = bs.doctor || (bs.professionalIds?.[0] || null);
    const profIds = doctor ? [doctor] : (bs.professionalIds || []);
    const ds = dayDate || selectedDateString;

    const payload = {
      // Flutter-style
      hourStart: bs.hourStart,
      hourStop: bs.hourStop,
      scheduleId: bs.slotId,
      bookingMode: bs.bookingMode,
      isOnDemand: bs.bookingMode === BookingMode.ON_DEMAND,
      isExtra: isExtraSlot,
      components: bs.components,
      date: ds,
      durationId: activeDuration?.id,
      durationMinutes: activeDuration?.durationMinutes,
      doctor,
      // Legacy compat
      id: bs.slotId,
      init: bs.hourStart,
      end: bs.hourStop,
      hour: bs.hourStart,
      duration_id: activeDuration?.id,
      professional_ids: profIds,
      professional_id: doctor?.id,
      full_date: ds,
      sectionLabel: isExtraSlot
        ? `Consulta extra${activeDuration?.durationMinutes ? ` — ${activeDuration.durationMinutes} min` : ''}`
        : `Consulta incluida${activeDuration?.durationMinutes ? ` — ${activeDuration.durationMinutes} min` : ''}`,
    };

    if (onTimeClick) onTimeClick(payload);
    else if (setSelectedSlot) setSelectedSlot(payload);
  };

  // El chip rail navega al día: si está en stackedDays hace scroll a su anchor.
  // También actualiza `selectedDate` (para resaltar el chip activo).
  const handleChipClick = (date) => {
    const ds = formatYmd(date);
    onDateSelect(date);
    setTimeout(() => {
      const el = dayAnchorRefs.current[ds];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-warm-canvas">
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-12">
        {/* Header tipo app: back + título + chip especialidad */}
        <div className="mb-4">
          <button onClick={onBack} className="press-scale mb-3 text-brand flex items-center gap-2 hover:gap-3 transition-all font-semibold">
            <ArrowLeft size={20} aria-hidden="true" /> Volver
          </button>
          <div className="flex items-start gap-2">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-brand-deep tracking-[-0.025em]">Agenda por fecha</h1>
              {specialty && (
                <div className="flex items-center gap-1.5 mt-1 text-daisies font-semibold text-sm">
                  <Briefcase size={14} />
                  <span>{specialty.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── Chip-rail horizontal de fechas ─── */}
        <section className="bg-daisies-50/40 rounded-card p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="text-daisies" size={18} />
            <h3 className="text-sm font-semibold text-text-primary">Elige una fecha</h3>
          </div>
          {/* Wrapper con fade derecho como hint visual de "hay más fechas". */}
          <div className="relative">
            <div
              ref={chipRailRef}
              className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide"
            >
              {next14Days.map((date) => {
                const ds = formatYmd(date);
                const isSelected = selectedDateString === ds;
                const dayLabel = DAY_LABELS_SHORT_ES[date.getDay()];

                // Estado del cache para este día (sólo si ya fue cargado).
                const dayCache = activeDuration?.id
                  ? scheduleCache[cacheKey(ds, activeDuration.id)]
                  : null;
                const isEmpty = dayCache && !dayCache.loading && !dayCache.error && (!dayCache.slots || dayCache.slots.length === 0);
                const hasSlots = dayCache && !dayCache.loading && !dayCache.error && dayCache.slots?.length > 0;

                return (
                  <button
                    key={ds}
                    ref={isSelected ? selectedChipRef : null}
                    onClick={() => handleChipClick(date)}
                    className={`press-scale flex-shrink-0 w-16 py-3 rounded-card transition-all border-2 text-center ${
                      isSelected
                        ? 'bg-daisies border-daisies text-white shadow-elevated'
                        : isEmpty
                          ? 'bg-white border-black/5 text-text-secondary opacity-60 hover:opacity-80'
                          : 'bg-white border-black/5 text-text-primary hover:border-daisies/40'
                    }`}
                  >
                    <div className={`text-[11px] font-medium uppercase tracking-wider ${isSelected ? 'text-white/90' : 'text-text-secondary'}`}>
                      {dayLabel}
                    </div>
                    <div className={`text-xl font-bold mt-0.5 ${isSelected ? 'text-white' : isEmpty ? 'text-text-secondary' : 'text-text-primary'}`}>
                      {date.getDate()}
                    </div>
                    {/* Indicador: punto blanco si seleccionado, • daisies si hay slots cacheados */}
                    {isSelected ? (
                      <div className="mt-1 mx-auto w-1.5 h-1.5 rounded-full bg-white" />
                    ) : hasSlots ? (
                      <div className="mt-1 mx-auto w-1.5 h-1.5 rounded-full bg-daisies" />
                    ) : (
                      <div className="mt-1 mx-auto w-1.5 h-1.5" />
                    )}
                  </button>
                );
              })}
            </div>
            {/* Fade derecho: indica que se puede scrollear sin un scrollbar feo. */}
            <div className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-daisies-50/40 via-daisies-50/40 to-transparent" />
          </div>
        </section>

        {/* ─── Selector de duración ─── */}
        {durationOptions.length > 1 && (
          <section className="bg-daisies-50/40 rounded-card p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="text-daisies" size={18} />
              <h3 className="text-sm font-semibold text-text-primary">Elige duración</h3>
            </div>
            <div className="relative bg-white rounded-full border-2 border-daisies/30 p-1 flex">
              {durationOptions.map((dur, idx) => {
                const isActive = activeDurationId === dur.id;
                // Recomendado vive SIEMPRE sobre la consulta principal (la larga
                // = primera tras el sort), no sobre la que esté seleccionada.
                // Esto evita que la usuaria piense que "la corta también es
                // recomendada" si la toca.
                const isRecommendedSlot = idx === 0;
                return (
                  <button
                    key={dur.id}
                    onClick={() => {
                      setActiveDurationId(dur.id);
                      if (setSelectedSlot) setSelectedSlot(null);
                    }}
                    className={`relative flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${
                      isActive ? 'bg-daisies text-white shadow-soft' : 'text-text-primary'
                    }`}
                  >
                    {isRecommendedSlot && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 ds-caption font-semibold px-2 py-0.5 bg-daisies text-white rounded-full whitespace-nowrap shadow-soft z-10">
                        Recomendado
                      </span>
                    )}
                    {dur.durationMinutes ? `${dur.durationMinutes} min` : dur.name}
                  </button>
                );
              })}
            </div>
            {activeDuration && (
              <div className="text-center text-xs text-daisies font-medium mt-3">
                {subCopyForDuration(activeDuration, activeDuration.id === durationOptions[0].id)}
              </div>
            )}
          </section>
        )}

        {/* ─── Lista de horarios — apilada por día (Flutter parity) ─── */}
        <section className="mt-2">
          <div className="flex items-center gap-2 mb-3 px-1">
            <div className="w-7 h-7 rounded-full bg-daisies/15 flex items-center justify-center">
              <Clock className="text-daisies" size={14} />
            </div>
            <h3 className="text-sm font-semibold text-text-primary">Horarios disponibles</h3>
          </div>

          {isLoading && stackedDays.length === 0 ? (
            <div className="py-8">
              <GlobalLoader title="Cargando horarios" subtitle="Buscando disponibilidad..." overlay={false} />
            </div>
          ) : hasError && stackedDays.length === 0 ? (
            <div className="text-center py-8 bg-red-50 rounded-card">
              <AlertCircle className="text-red-500 mx-auto mb-2" size={28} />
              <p className="text-red-600 text-sm mb-3">No se pudieron cargar los horarios.</p>
              <button
                onClick={() => fetchSchedule(selectedDateString, activeDuration)}
                className="press-scale px-4 py-2 bg-daisies text-white rounded-full font-semibold text-xs hover:bg-daisies-hover transition-all"
              >
                Reintentar
              </button>
            </div>
          ) : stackedDays.length === 0 ? (
            <EmptyState
              onJumpNext={() => {
                const idx = next14Days.findIndex(d => formatYmd(d) === selectedDateString);
                const target = next14Days[idx + 1] || null;
                if (target) handleChipClick(target);
              }}
              hasNext={selectedDateString && next14Days.findIndex(d => formatYmd(d) === selectedDateString) < next14Days.length - 1}
            />
          ) : (
            <div className="space-y-6">
              {stackedDays.map((day) => (
                <div
                  key={day.date}
                  ref={(el) => (dayAnchorRefs.current[day.date] = el)}
                  className="scroll-mt-4"
                >
                  <h4 className="text-lg font-bold text-text-primary mb-3 px-1">
                    {formatLongDate(day.date)}
                  </h4>
                  <div className="space-y-2.5">
                    {day.slots.map((bs, idx) => (
                      <SlotCard
                        key={`${day.date}-${bs.slotId}-${bs.doctor?.id || idx}-${bs.hourStart}`}
                        slot={bs}
                        isSelected={isSelectedSlot(selectedSlot, bs, day.date, activeDuration)}
                        hasMembership={hasMembership}
                        onClick={() => handleSlotClick(bs, day.date)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

// Helper: para comparar el slot seleccionado preserva entre re-renders.
const isSelectedSlot = (selectedSlot, bs, dateString, activeDuration) => {
  if (!selectedSlot) return false;
  return (
    selectedSlot.full_date === dateString &&
    selectedSlot.init === bs.hourStart &&
    selectedSlot.duration_id === activeDuration?.id &&
    selectedSlot.professional_id === bs.doctor?.id
  );
};

// "Viernes 15 de mayo"
const formatLongDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString + 'T00:00:00');
  const weekday = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][d.getDay()];
  return `${weekday} ${d.getDate()} de ${MONTH_LABELS_LONG_ES[d.getMonth()]}`;
};

// ─── Slot card horizontal (Flutter parity + UX optimizations) ───
//
// `slot.isLocked` (= `source === 'support'`) NO indica desactivación: indica
// que el horario fue cargado por el staff de Daisies y por tanto está
// GARANTIZADO. El slot es 100% bookable; lo marcamos visualmente con un sello
// "✓ Garantizado" para que la usuaria sepa que es un horario "seguro".
//
// La diferencia funcional es el `bookingMode`:
//   - immediate  → "Disponibilidad inmediata" (badge daisies + Zap)
//   - on_demand  → "Bajo confirmación"        (badge amber + Hourglass)
const SlotCard = ({ slot, isSelected, onClick }) => {
  const isOnDemand = slot.bookingMode === BookingMode.ON_DEMAND;
  const isStaffGuaranteed = slot.isLocked;
  const doctor = slot.doctor;
  const doctorName = doctor
    ? `${doctor.name || ''} ${doctor.last_name || ''}`.trim() || 'Doctora'
    : null;
  const rating = doctor?.rating_professional;

  const containerCls = isSelected
    ? 'bg-brand text-white'
    : isOnDemand
      ? 'bg-amber-pill/20 hover:bg-amber-pill/30 cursor-pointer'
      : 'bg-white hover:bg-brand-tint/40 cursor-pointer';

  const hourBoxCls = isSelected
    ? 'bg-white/15 text-white'
    : isOnDemand
      ? 'bg-amber-100/80 text-amber-800'
      : 'bg-daisies-50 text-daisies';

  return (
    <button
      onClick={onClick}
      className={`press-scale-card w-full text-left rounded-card transition-all p-3 flex items-stretch gap-3 ${containerCls}`}
    >
      {/* Caja de hora (izquierda) — icono según `bookingMode`:
            on-demand → reloj de arena (espera de confirmación)
            inmediato → rayo (confirma al instante) */}
      <div className={`flex flex-col items-center justify-center px-3 py-2 rounded-input min-w-[72px] ${hourBoxCls}`}>
        {isOnDemand ? (
          <Hourglass size={13} className={`mb-0.5 ${isSelected ? 'text-white' : 'text-amber-700'}`} />
        ) : (
          <Zap size={13} className={`mb-0.5 ${isSelected ? 'text-white' : 'text-daisies'}`} />
        )}
        <div className={`font-bold text-[17px] leading-none tabular-nums ${isSelected ? 'text-white' : isOnDemand ? 'text-amber-900' : 'text-text-primary'}`}>
          {slot.hourStart}
        </div>
        <div className={`text-[11px] mt-1 leading-none tabular-nums ${isSelected ? 'text-white/80' : isOnDemand ? 'text-amber-700/80' : 'text-text-secondary'}`}>
          {slot.hourStop}
        </div>
      </div>

      {/* Avatar + info doctora */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        {doctor?.avatar && (
          <img
            src={doctor.avatar}
            alt={doctorName || 'Doctora'}
            className={`w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 ${isSelected ? 'border-white/40' : 'border-white shadow-soft'}`}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorName || 'Dra')}&background=786ED9&color=fff`;
            }}
          />
        )}

        <div className="flex-1 min-w-0">
          {/* Badges contextuales (Flutter parity):
              - On-demand → amber "Bajo confirmación"
              - Immediate → daisies "Disponibilidad inmediata"
              - Staff-guaranteed (isLocked) → "✓ Garantizado"
              Ya no se muestran badges de "Con membresía" ni "Consulta extra":
              cualquier horario es agendable sin membresía. */}
          <div className="flex flex-wrap items-center gap-1 mb-1">
            {isOnDemand ? (
              <span className={`inline-flex items-center gap-1 ds-caption font-semibold px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-amber-pill text-amber-deep'}`}>
                <Hourglass size={10} aria-hidden="true" />
                Bajo confirmación
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1 ds-caption font-semibold px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-brand-tint text-brand-deep'}`}>
                <Zap size={10} aria-hidden="true" />
                Disponibilidad inmediata
              </span>
            )}
            {isStaffGuaranteed && !isOnDemand && (
              <span className={`inline-flex items-center gap-1 ds-caption font-semibold px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-mint-pill text-mint-deep'}`}>
                <Check size={10} aria-hidden="true" /> Garantizado
              </span>
            )}
          </div>

          <div className={`font-bold text-[15px] leading-tight truncate ${isSelected ? 'text-white' : 'text-text-primary'}`}>
            {doctorName || `${slot.professionalIds?.length || 0} doctoras disponibles`}
          </div>

          {/* Rating + CTA en una línea para densificar info útil */}
          <div className={`text-xs mt-0.5 flex items-center gap-2 ${isSelected ? 'text-white/85' : 'text-text-secondary'}`}>
            {rating ? (
              <span className="inline-flex items-center gap-0.5">
                <Star size={11} className={isSelected ? 'fill-white text-white' : 'fill-yellow-400 text-yellow-400'} />
                <span className="font-semibold">{Number(rating).toFixed(1)}</span>
              </span>
            ) : null}
            <span>Toca para agendar</span>
          </div>
        </div>
      </div>

      {/* Chevron derecho */}
      <div className="flex items-center justify-center">
        <ChevronRight
          size={20}
          className={isSelected ? 'text-white' : isOnDemand ? 'text-amber-700' : 'text-daisies'}
        />
      </div>
    </button>
  );
};

const EmptyState = ({ onJumpNext, hasNext }) => (
  <div className="text-center py-10 px-6 bg-surface rounded-card">
    <Calendar className="text-text-secondary mx-auto mb-3" size={36} />
    <p className="text-text-primary text-sm font-semibold mb-1">Sin horarios en este día</p>
    <p className="text-text-secondary text-xs mb-4">Prueba otra fecha del calendario</p>
    {hasNext && (
      <button
        onClick={onJumpNext}
        className="press-scale inline-flex items-center gap-1 px-4 py-2 bg-daisies text-white rounded-full font-semibold text-xs hover:bg-daisies-hover transition-all shadow-soft"
      >
        Ver siguiente día <ChevronRight size={14} />
      </button>
    )}
  </div>
);

export default CalendarSelection;
